import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeftIcon,
  CodeBracketIcon,
  LightBulbIcon,
  BookOpenIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'

const QuestionBankTypePage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const category = location.state?.category || { collegeType: 'engineering', tier: 'tier1' }

  const handleTypeSelect = (assessmentType: string) => {
    // Route to specialized coding question page for coding assessments
    if (assessmentType === 'coding') {
      navigate('/recruiter/coding-questions/create', {
        state: { category, assessmentType }
      })
    } else {
      // Use existing simple form for other types
      navigate('/recruiter/question-bank/add', {
        state: { category, assessmentType }
      })
    }
  }

  const getAssessmentTypes = () => {
    if (category.collegeType === 'management') {
      return [
        {
          id: 'quantitative_ability',
          title: 'Quantitative Ability',
          icon: LightBulbIcon,
          color: 'indigo',
          description: 'Numerical and quantitative aptitude questions'
        },
        {
          id: 'logical_reasoning',
          title: 'Logical Reasoning',
          icon: LightBulbIcon,
          color: 'green',
          description: 'Logical and analytical reasoning questions'
        },
        {
          id: 'verbal_ability',
          title: 'Verbal Ability',
          icon: BookOpenIcon,
          color: 'blue',
          description: 'English language and verbal comprehension questions'
        }
      ]
    } else {
      return [
        {
          id: 'coding',
          title: 'Coding Problems',
          icon: CodeBracketIcon,
          color: 'blue',
          description: 'Algorithmic and programming problems'
        },
        {
          id: 'aptitude',
          title: 'Aptitude & Reasoning',
          icon: LightBulbIcon,
          color: 'green',
          description: 'Logical reasoning and aptitude questions'
        },
        {
          id: 'fundamentals',
          title: 'Computer Fundamentals',
          icon: BookOpenIcon,
          color: 'purple',
          description: 'Core computer science knowledge questions'
        },
        {
          id: 'cloud_fundamentals',
          title: 'Cloud Fundamentals',
          icon: BookOpenIcon,
          color: 'sky',
          description: 'Cloud computing and infrastructure knowledge'
        },
        {
          id: 'presales_fundamentals',
          title: 'Pre-Sales Fundamentals',
          icon: BookOpenIcon,
          color: 'orange',
          description: 'Pre-sales and solution engineering knowledge'
        },
        {
          id: 'english_communication',
          title: 'English Communication',
          icon: LightBulbIcon,
          color: 'teal',
          description: 'English language proficiency and communication skills'
        },
        {
          id: 'ai_fundamental',
          title: 'AI Fundamental',
          icon: BookOpenIcon,
          color: 'violet',
          description: 'Artificial Intelligence fundamentals and core concepts'
        },
        {
          id: 'aiml_assignment',
          title: 'AI/ML Assignment',
          icon: DocumentTextIcon,
          color: 'pink',
          description: 'AI/ML project assignments with link submission'
        }
      ]
    }
  }

  const assessmentTypes = getAssessmentTypes()

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/recruiter/question-bank')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors mr-4"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Categories
            </button>
          </div>
          
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Select Assessment Type</h1>
            <p className="text-gray-600 mt-2">
              {category.collegeType === 'engineering' ? 'Engineering' : 'Management'} - 
              {category.tier === 'tier1' ? ' Tier 1' : ' Tier 2'}
            </p>
          </div>
        </div>

        {/* Assessment Type Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {assessmentTypes.map((type) => {
            const IconComponent = type.icon
            return (
              <div 
                key={type.id}
                className={`card p-6 hover:border-${type.color}-300 hover:shadow-lg transition-all cursor-pointer group`}
                onClick={() => handleTypeSelect(type.id)}
              >
                <div className="flex items-center mb-4">
                  <div className={`p-3 bg-${type.color}-100 rounded-lg mr-4 group-hover:bg-${type.color}-200 transition-colors`}>
                    <IconComponent className={`w-8 h-8 text-${type.color}-600`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{type.title}</h3>
                  </div>
                </div>
                <p className="text-gray-600 mb-4">{type.description}</p>
                <div className={`flex items-center text-${type.color}-600 font-medium group-hover:translate-x-1 transition-transform`}>
                  <span>Select Type</span>
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default QuestionBankTypePage

